<template>
  <div>
    <h3>Unsubmitted Faculty</h3>
    <span>This is a list of faculty who have not submitted any workloads for the
      selected time period. Use the dropdown to change time periods.</span>

    <b-form inline @submit.prevent="" class="mb-2">
      <b-input-group class="filter-group">
        <b-input-group-prepend is-text>
          <font-awesome-icon icon="search" />
        </b-input-group-prepend>
        <b-form-input v-model="filters.search" placeholder="Type to Search" debounce="500"
          @input="searchItems"></b-form-input>
        <b-input-group-append>
          <b-button @click="clearFilter()">Clear</b-button>
        </b-input-group-append>
      </b-input-group>

      <b-form-select v-model="filters.timePeriodId" :options="timePeriods" value-field="timePeriodId"
        text-field="description" @input="filterItems" class="mb-2 mx-2"></b-form-select>

      <b-form-checkbox @input="filterItems" v-model="filters.chsOnly" class="font-weight-bold">CHS
        Only</b-form-checkbox>
    </b-form>
    <div class="ml-2 mb-2">
      <b-row>
        <b-col sm="3">
          <b-form-checkbox-group v-model="filters.paygroups" :options="paygroupOptions" @input="filterItems">
          </b-form-checkbox-group>
        </b-col>
        <b-col sm="9">
          <b-form-checkbox-group v-model="filters.tenureStatuses" :options="tenureStatusOptions" @input="filterItems">
          </b-form-checkbox-group>
        </b-col>
      </b-row>
    </div>

    <b-table striped hover no-provider-filtering no-provider-paging no-provider-sorting no-local-sorting
      :items="unsubmittedFaculty" :fields="fields" @sort-changed="changeSort" responsive>
      <template v-slot:cell(name)="data">
        {{ data.item.lastName }}, {{ data.item.firstName }}
      </template>
      <template v-slot:cell(jobcodeDescr)="data">
        <div v-for="(job, index) in data.item.employeeJobs" :key="index">
          {{ job.job.description }} - {{ job.department.description }}
          <span v-if="job.jobIndicator == 'P'">(Primary)</span>
          - FTE: {{ job.fte }}
        </div>
      </template>
      <template v-slot:cell(details)="data">
        <span v-if="isWorkloadsManager">
          <router-link :to="{
            name: 'workloadAddBehalf',
            params: { asurite: data.item.asuriteId },
            query: { timePeriod: currentTimePeriod.timePeriodId },
          }">Submit {{ currentTimePeriod.description }}</router-link>
          |
          <router-link :to="{
            name: 'workloadAddBehalf',
            params: { asurite: data.item.asuriteId },
            query: { timePeriod: nextTimePeriod.timePeriodId },
          }">Submit {{ nextTimePeriod.description }}</router-link>
        </span>
      </template>
    </b-table>

    <b-pagination @change="changePage" :total-rows="totalRows" v-model="currentPage" :per-page="perPage"
      align="right" />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import roleChecks from "../../misc/role-checks";

import employeesApi from "../../api/employees";
import debounce from "lodash/debounce";

export default {
  mixins: [roleChecks],
  data() {
    return {
      fields: [
        { key: "name", sortable: true },
        { key: "asuriteId", sortable: true },
        { key: "jobcodeDescr", sortable: true },
        { key: "email", sortable: true },
        { key: "details", sortable: false, label: "" }
      ],

      unsubmittedFaculty: [],

      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      sortBy: "name",
      sortDesc: false,

      filters: {
        timePeriodId: 0,
        chsOnly: true,
        search: "",
        tenureStatuses: [],
        paygroups: []
      }, // these are getting passed to the API AND THE NAMES MUST BE THE SAME!!!

      paygroupOptions: [
        { text: "Academic Year", value: "A12,ACD" }, // { items: ["A12", "ACD"] } },
        { text: "Fiscal Year", value: "FSW,SAL" } // { items: ["FSW", "SAL"] } },
      ],
      tenureStatusOptions: [
        { text: "Tenure-Track", value: "TRK" },
        { text: "Tenure", value: "TEN" },
        { text: "Not on Track", value: "NTR" }
      ]
    };
  },

  computed: {
    ...mapState({
      timePeriods: state => state.timePeriods.timePeriods,
      currentTimePeriod: state => state.status.currentTimePeriod,
      nextTimePeriod: state => state.status.nextTimePeriod,
      fiscalYearTimePeriod: state => state.status.fiscalYearTimePeriod
    })
  },

  watch: {},

  methods: {
    ...mapActions("timePeriods", ["getTimePeriods"]),

    ...mapActions("status", [
      "getFiscalYearTimePeriod",
      "getNextTimePeriod",
      "getCurrentTimePeriod"
    ]),

    // triggered from <b-table @sort-changed="changeSort"
    changeSort(context) {
      this.sortBy = context.sortBy;
      this.sortDesc = context.sortDesc;
      this.updateItems();
    },
    // triggered from <b-pagination @change="changePage"
    changePage(page) {
      this.currentPage = page;
      this.updateItems();
    },
    filterItems() {
      this.currentPage = 1;
      this.unsubmittedFaculty = [];
      this.updateItems();
    },

    searchItems: debounce(function () {
      this.filterItems();
    }, 500),

    updateItems() {
      employeesApi
        .getUnsubmittedFaculty(
          this.filters,
          this.currentPage,
          this.perPage,
          this.sortBy,
          this.sortDesc
        )
        .then(response => {
          this.unsubmittedFaculty = response.data;

          var meta = JSON.parse(response.headers["x-pagination"]);
          this.totalRows = meta.TotalItems;
          if (!response.data.length) {
            this.noResults = true;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    clearFilter: function () {
      this.filters.search = "";
      this.updateItems();
    }
  },

  created() {
    var that = this;
    this.getCurrentTimePeriod();
    this.getNextTimePeriod();

    this.getTimePeriods().then(function () {
      that.getFiscalYearTimePeriod().then(function () {
        that.filters.timePeriodId = that.fiscalYearTimePeriod.timePeriodId;
      });
    });
  }
};
</script>

<style scoped>
.filter-group {
  margin: 10px 0 15px 0;
  max-width: 600px;
}
</style>
